/**
 * SponsorTypesEditor Component
 *
 * Entity-specific editor for SponsorTypes lookup table
 * Includes sequence number input, is_required checkbox, and drag-and-drop reordering
 */

import {
  useSponsorTypes,
  useCreateSponsorType,
  useUpdateSponsorType,
  useDeleteSponsorType,
} from '@medsuite/api-client';
import { Button, Alert, Spinner, useDialog } from '@medsuite/ui-components';
import { useState, useEffect } from 'react';

interface SponsorType {
  sponsor_type_id: number;
  sponsor_type_name: string;
  sequence: number;
  is_required: boolean;
}

interface SponsorTypesEditorProps {
  onDataLoaded?: (count: number) => void;
}
export default function SponsorTypesEditor({ onDataLoaded }: SponsorTypesEditorProps) {
  const { data: sponsorTypes = [], isLoading, error } = useSponsorTypes();
  const addMutation = useCreateSponsorType();
  const updateMutation = useUpdateSponsorType();
  const deleteMutation = useDeleteSponsorType();

  const [isAddingNew, setIsAddingNew] = useState(false);
  const [newItem, setNewItem] = useState({
    sponsor_type_name: '',
    sequence: 0,
    is_required: false,
  });
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editingItem, setEditingItem] = useState<Partial<SponsorType>>({});
  const [draggedId, setDraggedId] = useState<number | null>(null);

  // Dialog hooks
  const { confirm } = useDialog();

  // Sort by sequence
  const sortedData = [...sponsorTypes].sort((a, b) => a.sequence - b.sequence);

  // Notify parent of data count
  useEffect(() => {
    if (onDataLoaded && sponsorTypes) {
      onDataLoaded(sponsorTypes.length);
    }
  }, [sponsorTypes, onDataLoaded]);

  // Handle add
  const handleSaveAdd = async () => {
    try {
      await addMutation.mutateAsync(newItem);
      setIsAddingNew(false);
      setNewItem({ sponsor_type_name: '', sequence: 0, is_required: false });
    } catch (err) {
      console.error('Failed to add:', err);
    }
  };

  // Handle edit
  const handleSaveEdit = async () => {
    if (!editingId) {
      return;
    }
    try {
      await updateMutation.mutateAsync({ id: editingId, data: editingItem });
      setEditingId(null);
      setEditingItem({});
    } catch (err) {
      console.error('Failed to update:', err);
    }
  };

  // Handle delete
  const handleDelete = async (id: number) => {
    const confirmed = await confirm({
      title: 'Delete Sponsor Type',
      message: 'Are you sure you want to delete this item?',
      variant: 'danger',
      confirmText: 'Delete',
    });
    if (!confirmed) {
      return;
    }
    try {
      await deleteMutation.mutateAsync(id);
    } catch (err) {
      console.error('Failed to delete:', err);
    }
  };

  // Handle drag and drop
  const handleDragStart = (id: number) => {
    setDraggedId(id);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const handleDrop = async (targetId: number) => {
    if (!draggedId || draggedId === targetId) {
      setDraggedId(null);
      return;
    }

    const draggedItem = sortedData.find((item) => item.sponsor_type_id === draggedId);
    const targetItem = sortedData.find((item) => item.sponsor_type_id === targetId);

    if (!draggedItem || !targetItem) {
      setDraggedId(null);
      return;
    }

    try {
      // Swap sequences
      await updateMutation.mutateAsync({
        id: draggedId,
        data: { sequence: targetItem.sequence },
      });
      await updateMutation.mutateAsync({
        id: targetId,
        data: { sequence: draggedItem.sequence },
      });
    } catch (err) {
      console.error('Failed to reorder:', err);
    }

    setDraggedId(null);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-12">
        <Spinner />
      </div>
    );
  }

  if (error) {
    return <Alert variant="error">Failed to load sponsor types: {error.message}</Alert>;
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-end">
        {!isAddingNew && (
          <Button variant="primary" onClick={() => setIsAddingNew(true)}>
            Add New
          </Button>
        )}
      </div>

      <div className="overflow-x-auto border border-gray-200 dark:border-gray-700 rounded-lg">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-800">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                ID
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                Name
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                Sequence
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                Required
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
            {/* Add New Row */}
            {isAddingNew && (
              <tr className="bg-blue-50 dark:bg-blue-950">
                <td className="px-6 py-4">
                  <span className="text-sm text-gray-500 dark:text-gray-400 italic">
                    Auto-generated
                  </span>
                </td>
                <td className="px-6 py-4">
                  <input
                    type="text"
                    value={newItem.sponsor_type_name}
                    onChange={(e) => setNewItem({ ...newItem, sponsor_type_name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:focus:ring-indigo-400"
                  />
                </td>
                <td className="px-6 py-4">
                  <input
                    type="number"
                    value={newItem.sequence}
                    onChange={(e) => setNewItem({ ...newItem, sequence: parseInt(e.target.value) })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:focus:ring-indigo-400"
                  />
                </td>
                <td className="px-6 py-4">
                  <input
                    type="checkbox"
                    checked={newItem.is_required}
                    onChange={(e) => setNewItem({ ...newItem, is_required: e.target.checked })}
                    className="h-4 w-4 text-indigo-600 dark:text-indigo-400"
                  />
                </td>
                <td className="px-6 py-4 text-right">
                  <button
                    onClick={() => setIsAddingNew(false)}
                    className="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 mr-3"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSaveAdd}
                    className="text-indigo-600 dark:text-indigo-400 hover:text-indigo-900 dark:hover:text-indigo-300"
                  >
                    Save
                  </button>
                </td>
              </tr>
            )}

            {/* Data Rows */}
            {sortedData.map((item) => {
              const isEditing = editingId === item.sponsor_type_id;
              return (
                <tr
                  key={item.sponsor_type_id}
                  draggable={!isEditing}
                  onDragStart={() => handleDragStart(item.sponsor_type_id)}
                  onDragOver={handleDragOver}
                  onDrop={() => handleDrop(item.sponsor_type_id)}
                  className={`${isEditing ? 'bg-yellow-50 dark:bg-yellow-950' : ''} ${!isEditing ? 'cursor-move' : ''}`}
                >
                  <td className="px-6 py-4 text-sm text-gray-900 dark:text-gray-100">
                    {item.sponsor_type_id}
                  </td>
                  <td className="px-6 py-4">
                    {isEditing ? (
                      <input
                        type="text"
                        value={editingItem.sponsor_type_name || ''}
                        onChange={(e) =>
                          setEditingItem({ ...editingItem, sponsor_type_name: e.target.value })
                        }
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:focus:ring-indigo-400"
                      />
                    ) : (
                      <div className="text-sm text-gray-900 dark:text-gray-100">
                        {item.sponsor_type_name}
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    {isEditing ? (
                      <input
                        type="number"
                        value={editingItem.sequence ?? ''}
                        onChange={(e) =>
                          setEditingItem({ ...editingItem, sequence: parseInt(e.target.value) })
                        }
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:focus:ring-indigo-400"
                      />
                    ) : (
                      <div className="text-sm text-gray-900 dark:text-gray-100">
                        {item.sequence}
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    {isEditing ? (
                      <input
                        type="checkbox"
                        checked={editingItem.is_required ?? false}
                        onChange={(e) =>
                          setEditingItem({ ...editingItem, is_required: e.target.checked })
                        }
                        className="h-4 w-4 text-indigo-600 dark:text-indigo-400"
                      />
                    ) : (
                      <span className="text-sm text-gray-900 dark:text-gray-100">
                        {item.is_required ? '✓' : ''}
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 text-right">
                    {isEditing ? (
                      <>
                        <button
                          onClick={() => setEditingId(null)}
                          className="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 mr-3"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={handleSaveEdit}
                          className="text-indigo-600 dark:text-indigo-400 hover:text-indigo-900 dark:hover:text-indigo-300"
                        >
                          Save
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          onClick={() => {
                            setEditingId(item.sponsor_type_id);
                            setEditingItem(item);
                          }}
                          className="text-indigo-600 dark:text-indigo-400 hover:text-indigo-900 dark:hover:text-indigo-300 mr-3"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDelete(item.sponsor_type_id)}
                          className="text-red-600 dark:text-red-400 hover:text-red-900 dark:hover:text-red-300"
                        >
                          Delete
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
